/**
 * SharedIndicator Component
 *
 * Displays a "Shared (N)" chip when a playbook appears in multiple strategies.
 * Click/tap opens a popover listing which strategies contain this playbook.
 * Works on both desktop and mobile.
 *
 * Note: Uses a wrapper span with role="button" to avoid nested button issues
 * when rendered inside PlaybookListItem's button element.
 */

import React, { useMemo } from 'react';

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Chip,
} from '@enfomentis/ui';

export interface SharedIndicatorProps {
  /** Array of strategy names that contain this playbook */
  strategyNames: string[];
}

export const SharedIndicator: React.FC<SharedIndicatorProps> = ({ strategyNames }) => {
  const count = strategyNames.length;

  // Sort strategy names alphabetically for consistent display
  const sortedNames = useMemo(
    () => [...strategyNames].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    [strategyNames]
  );

  // Only show when playbook is shared across 2+ strategies
  if (count < 2) {
    return null;
  }

  // Handle keyboard activation (Enter/Space) for accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      // The PopoverTrigger will handle opening via the click event
      (e.target as HTMLElement).click();
    }
  };

  // Prevent click from bubbling to parent button (row selection)
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Popover isNonModal placement="bottom-start">
      <PopoverTrigger>
        <span
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          className="shrink-0"
        >
          <Chip
            className="text-xs text-text-secondary bg-default-100 cursor-pointer hover:bg-default-200 transition-colors"
            size="sm"
          >
            Shared ({count})
          </Chip>
        </span>
      </PopoverTrigger>
      <PopoverContent className="p-3">
        <div className="w-72 space-y-1">
          <p className="text-xs font-medium text-text-secondary mb-2">
            Shared with:
          </p>
          <ul className="space-y-1 max-h-48 overflow-y-auto min-w-0">
            {sortedNames.map((name, index) => (
              <li
                key={index}
                className="text-sm text-text-primary truncate w-full"
                title={name}
              >
                {name}
              </li>
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
};
